import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, X } from 'lucide-react';
import { FormFieldRenderer } from '@/components/forms/FormFieldRenderer';
import { LayoutTabularSection } from '@/components/autoforms/LayoutTabularSection';
import { LayoutField } from '@/components/autoforms/LayoutField';
import { detectFieldType, buildTypesFromTypeString } from '@/lib/formTypes';
import { FileMultiField } from '@/components/autoforms/FileMultiField';
import { ReferencePickerField } from '@/components/forms/ReferencePickerField';
import { MiniDynamicList } from '@/components/forms/MiniDynamicList';
import { DynamicListTable } from '@/components/forms/DynamicListTable';
import { RegisterRecordTable } from '@/components/autoforms/RegisterRecordTable';
import { useResponsive, isVisible as isVisibleOnDevice } from '@/hooks/useResponsive';
import { useFormValues, useFormValue } from '@/lib/formStore';
import { setFormValue } from '@/lib/formApi';
import { resolveDlFilters } from '@/lib/dlFilters';
import { isAuditField } from '@/lib/auditFields';

// Проверка на UUID (ссылочные типы 1C)
function isUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

// РАЗМЕЩЕНИЕ элемента (placement) относительно родителя (форма/группа):
// '' (По умолчанию) — как обычно; top/left — обычное положение;
// bottom — прижать к НИЗУ (в вертикальной группе/форме — marginTop:auto;
// в горизонтальной — выравнивание по низу); right — прижать к ПРАВОМУ краю
// (в горизонтальной — marginLeft:auto; в вертикальной — выравнивание вправо).
// На мобильных (вертикальная колонка) left/right вырождаются в выравнивание —
// группы остаются в потоке (никаких фоллбэков, только CSS).
function placementStyle(placement: string | undefined, parentDirection: 'horizontal' | 'vertical'): CSSProperties {
  const p = placement || '';
  if (p === 'top' || p === 'left') return {};
  if (parentDirection === 'vertical') {
    if (p === 'bottom') return { marginTop: 'auto' };
    if (p === 'right') return { alignSelf: 'flex-end' };
  } else {
    if (p === 'bottom') return { alignSelf: 'flex-end' };
    if (p === 'right') return { marginLeft: 'auto' };
  }
  return {};
}

function effectiveReadOnly(element: any, parentReadOnly: boolean): boolean {
  return parentReadOnly || element.readOnly || element.readonly || element.viewOnly || false;
}

function effectiveEnabled(element: any, parentEnabled: boolean): boolean {
  return parentEnabled && (element.enabled !== false);
}

/** Рендер значения поля по режиму вывода (Вид) элемента: поле/надпись/многострочное/чекбокс */
function renderByViewMode(el: any, value: unknown, onChange: (v: unknown) => void, disabled: boolean, readOnly: boolean) {
  const vm = el.viewMode || 'input';
  const text = String(value ?? '');
  switch (vm) {
    case 'label':
      return <div className="text-sm min-h-8 flex items-center">{text || '—'}</div>;
    case 'textarea':
      return (
        <textarea
          value={text}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          readOnly={readOnly}
          className="w-full rounded-md border bg-input px-3 py-2 text-sm h-20"
        />
      );
    case 'checkbox':
    case 'switch':
      return (
        <input
          type="checkbox"
          checked={value === true || value === 'true' || value === '1' || value === 1}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="h-4 w-4"
        />
      );
    default:
      return (
        <Input
          value={text}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 text-sm"
          disabled={disabled}
          readOnly={readOnly}
        />
      );
  }
}

interface CustomLayoutRendererProps {
  /** Ключ формы в formStore — ЕДИНЫЙ источник значений (значения полей читаются
   *  из стора, любые изменения идут через formApi.setFormValue → стор + шина
   *  form.value_changed). БЕЗ проп-цепочки values/setValues. */
  formKey: string;
  elements: any[];
  displayNames: Record<string, string>;
  setDisplayNames: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  tsData: Record<string, Array<Record<string, unknown>>>;
  setTsData: (fn: any) => void;
  attributes: any[];
  tabularSections?: Array<{ name: string; synonym: string; attributes: any[] }>;
  formReadOnly?: boolean;
  formEnabled?: boolean;
  hiddenFields?: Record<string, boolean>;
  onPick: (attr: any, tsName?: string, rowIndex?: number) => void;
  /** «Создать» в дин.списке: форма объекта источника + prefill из отборов */
  onPickTs?: (tsName: string, rowIndex: number, colAttr: any) => void;
  /** «Создать» в дин.списке: форма объекта источника + prefill из отборов */
  onCreateElement?: (table: string, filters: Array<{ field: string; condition: string; value?: string }>, objectId: string) => void;
  /** Клик по строке дин.списка: открыть запись источника (существующую) */
  onOpenElement?: (table: string, id: string) => void;
  /** «Поле множественного значения» (ТЧ вид='Поле'): выбор значения → новая строка ТЧ */
  onPickMulti?: (tsName: string, colMeta: any) => void;
  onCommand?: (command: string) => void;
  /** Реквизиты формы (для dynamic_list нужен source) */
  formAttrs?: Array<{ name: string; source?: { type?: string; table?: string; filters?: Array<{ field: string; condition: string; value?: string }>; commands?: string[] }; refCode?: string; multiple?: boolean; dimensionLinks?: Array<{ code: string; source: '' | 'current_object' | 'field'; field?: string }> }>;
  /** Id текущей записи формы (для связей измерений current_object) */
  recordId?: string;
}

export function CustomLayoutRenderer({
  formKey, elements, displayNames, setDisplayNames,
  tsData, setTsData, attributes, tabularSections, formReadOnly = false, formEnabled = true, hiddenFields = {}, onPick, onPickTs, onPickMulti, onCommand, formAttrs, recordId, onCreateElement, onOpenElement
}: CustomLayoutRendererProps) {
  const { device } = useResponsive();
  // Значения полей — ТОЛЬКО из formStore (единый источник; изменения — через
  // formApi.setFormValue → стор + шина). Проп-цепочки values/setValues нет.
  const values = useFormValues(formKey);

  // Защита от рекурсивного рендера макета (вложенные группы/страницы):
  // повреждённый layout (группа внутри себя, цикл вложенности) → переполнение
  // стека («Maximum call stack size exceeded») роняло ВСЁ приложение.
  // MAX_LAYOUT_DEPTH=15 — больше, чем любая разумная форма; превышение —
  // ЯВНАЯ ошибка (не молчаливый обрыв).
  const MAX_LAYOUT_DEPTH = 15;
  // Группа БЕЗ видимого содержимого (все дети — системные аудит-поля/скрытые
  // поля) НЕ рендерится вовсе: пустая рамка не остаётся. Аудит-поля показывает
  // AuditStatusBar в самом низу формы (ObjectFormBody) — единая точка.
  const hasVisibleContent = (el: any): boolean => {
    if (!el) return false;
    if (el.type === 'actionButton') return false;
    if (el.type === 'field') {
      const key = (el.attribute || '').split('.').pop() || el.attribute || '';
      return !isAuditField(key) && !isAuditField(el.attribute || '') && !hiddenFields[el.attribute];
    }
    if (el.type === 'group') return (el.elements || []).some(hasVisibleContent);
    return true;
  };
  const renderElement = (el: any, idx: number, parentReadOnly: boolean, parentEnabled: boolean, inPages = false, depth = 0): React.ReactNode => {
    if (depth > MAX_LAYOUT_DEPTH) {
      console.error('Layout depth exceeded (макет формы повреждён?)', el);
      return (
        <div key={`depth-${idx}`} className="text-xs text-destructive border border-destructive/30 rounded p-2 bg-destructive/5">
          Ошибка: слишком глубокая вложенность макета формы (более {MAX_LAYOUT_DEPTH} уровней).
        </div>
      );
    }
    const readOnly = effectiveReadOnly(el, parentReadOnly);
    const enabled = effectiveEnabled(el, parentEnabled);
    const disabled = !enabled || readOnly; // readOnly эквивалентно disabled для полей ввода

    switch (el.type) {
      case 'dynamicList': {
        const attr = (formAttrs || []).find((a) => a.name === el.attribute);
        const source = attr?.source || el.source;
        const table = source?.table || (source as any)?.table || '';
        // Отборы — ЕДИНЫЙ резолвер (lib/dlFilters, SSoT): @objectId → id текущей
        // записи, Объект.<поле> → значение формы; НЕЗАПОЛНЕННАЯ привязка даёт
        // `empty` — список ЗАВЕДОМО пуст (раньше пустой @objectId отбрасывался →
        // «Задачи по заявке» в новой заявке показывал ВСЕ задачи базы).
        const dlResolved = resolveDlFilters((source as any)?.filters, { recordId, formValues: values });
        const isQuerySource = source?.type === 'query' && !!source?.query;
        const dlCols = (el.columns || []).map((c: any) => ({
          field: (c.field || '').split('.').pop() || '',
          // label — то, что читает MiniDynamicList (заголовок колонки); synonym
          // оставлен для DynamicListTable (query-источники). РАНЬШЕ передавался
          // только synonym → заголовки падали в СЫРЫЕ имена колонок
          // (name/description/status_code вместо «Тема/Описание/Статус»).
          label: c.synonym || c.field,
          synonym: c.synonym || c.field,
          width: c.width,
        }));
        return (
          <div key={`dl-${idx}`} className={disabled ? 'opacity-60 pointer-events-none' : ''}>
            {el.label && el.showLabel !== false && <Label className="text-xs">{el.label}</Label>}
            {isQuerySource ? (
              <DynamicListTable
                query={source?.query}
                columns={dlCols}
              />
            ) : table ? (
              <MiniDynamicList
                table={table}
                onCreate={
                  // «Создать» → форма объекта ИСТОЧНИКА (справочник/документ/тип задачи)
                  // с предзаполнением из отборов списка (@objectId → текущая запись).
                  // Кнопка активна только для СОХРАНЁННОЙ записи (есть recordId/objectId)
                  // и НЕ для заведомо пустого отбора (незаполненная привязка) —
                  // SSoT: id записи — это server-атрибут, не содержимое customValues.
                  recordId && !dlResolved.empty ? () => onCreateElement?.(table, source?.filters || [], String(recordId)) : undefined
                }
                columns={dlCols.length > 0 ? dlCols : (el.columns || []).map((c: any) => ({ field: c.field, label: c.synonym || c.field }))}
                filters={dlResolved.filters}
                forceEmpty={dlResolved.empty}
                height={el.height}
                viewMode={el.viewMode as any}
                disabled={disabled}
                onRowClick={onOpenElement ? (id) => onOpenElement(table, id) : undefined}
              />
            ) : (
              <div className="text-xs text-muted-foreground p-2 border rounded bg-muted/20">
                Динамический список: не задан источник (таблица или запрос)
              </div>
            )}
          </div>
        );
      }
      case 'registerRecord': {
        // Реквизит «РегистрСведенийМенеджерЗаписи» (набор записей, как в 1С):
        // multiple=true → таблица строк регистра (RegisterRecordTable);
        // иначе поля по пути «Реквизит.Поле» рендерятся обычными полями.
        const attr = (formAttrs || []).find((a: any) => a.name === el.attribute);
        const multiple = !!(attr as any)?.multiple;
        if (!multiple) {
          return (
            <div key={`rr-${idx}`} className="text-[10px] text-muted-foreground border rounded p-2 bg-muted/20">
              Регистр сведений: {attr?.refCode || ''} (поля — по пути «{el.attribute}.Поле»)
            </div>
          );
        }
        if (!attr?.refCode) {
          return (
            <div key={`rr-${idx}`} className="text-[10px] text-amber-600 border rounded p-2 bg-amber-50">
              Набор записей: не выбран регистр сведений (свойства реквизита)
            </div>
          );
        }
        return (
          <div key={`rr-${idx}`} className={`space-y-1 ${disabled ? 'opacity-70 pointer-events-none' : ''}`}>
            {el.label && el.showLabel !== false && <Label className="text-xs">{el.label}</Label>}
            <RegisterRecordTable
              registerCode={attr.refCode}
              dimensionLinks={(attr as any)?.dimensionLinks}
              context={{ values, recordId }}
              disabled={disabled}
              onError={(msg) => console.error('[RegisterRecordTable]', msg)}
            />
          </div>
        );
      }
      case 'field': {
        // ЕДИНЫЙ рендер поля (вынесен в LayoutField — снижение цикломатики)
        return (
          <LayoutField
            el={el}
            idx={idx}
            formKey={formKey}
            values={values}
            displayNames={displayNames}
            setDisplayNames={setDisplayNames}
            attributes={attributes}
            hiddenFields={hiddenFields}
            disabled={disabled}
            readOnly={readOnly}
            onPick={onPick}
            renderByViewMode={renderByViewMode}
          />
        );
      }

      case 'button': {
        const handleClick = () => {
          if (disabled) return;
          if (el.onClick) {
            onCommand?.(el.onClick);
          } else if (el.command) {
            onCommand?.(el.command);
          }
        };
        return (
          <div key={`b-${idx}`} className="py-1" style={el.width ? { width: el.width } : undefined}>
            <Button size="sm" variant={el.variant === 'primary' ? 'default' : el.variant === 'danger' ? 'destructive' : 'outline'} onClick={handleClick} disabled={disabled}>
              {el.label || el.command || el.onClick || 'Кнопка'}
            </Button>
          </div>
        );
      }

      case 'actionButton':
        return null; // Рендерится в action panel

      case 'group': {
        const children = el.elements || [];
        // Пустая группа (только аудит/скрытые поля) — не рендерим (нет пустой рамки)
        if (!children.some(hasVisibleContent)) return null;
        // ВИД «Страницы»: вкладки-страницы (заголовок = title ребёнка); значения
        // страниц сохраняются (values/tsData — общие, страницы просто скрыты)
        if (el.viewMode === 'pages') {
          return (
            <GroupPagesView
              key={`g-${idx}`}
              elements={children}
              disabled={disabled}
              renderChild={(child: any, ci: number) => renderElement(child, ci, readOnly, enabled, true, depth + 1)}
            />
          );
        }
        // ВИД «Карточка»: карточка-обёртка (рамка/заголовок вокруг группы)
        const cardWrap = el.viewMode === 'card';
        return (
          <div key={`g-${idx}`} className={`flex flex-col gap-2 ${disabled ? 'opacity-60' : ''}`}>
            {el.title && el.showLabel !== false && !(inPages && el.viewMode === 'page') && <div className="text-xs font-medium text-muted-foreground">{el.title}</div>}
            <div className={`flex gap-2 ${cardWrap ? 'border rounded-lg p-3 bg-background shadow-sm' : ''} ${el.direction === 'horizontal' ? `flex-row ${el.wrap ? 'flex-wrap' : 'flex-nowrap overflow-x-auto'}` : 'flex-col'} border rounded-lg p-3`}>
              {children.map((child: any, ci: number) => (
                <div
                  key={`gc-${ci}`}
                  style={placementStyle((child as any).placement, el.direction === 'horizontal' ? 'horizontal' : 'vertical')}
                  className="min-w-0 min-h-0"
                >
                  {renderElement(child, ci, readOnly, enabled, false, depth + 1)}
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'tabularSection': {
        const tsName = el.name;
        const rows = tsData[tsName] || [];
        const allColsMeta = el.allColumns || [];
        const tsMeta = (tabularSections || []).find((ts: any) => ts.name === tsName);
        return (
          <LayoutTabularSection
            el={el}
            idx={idx}
            tsName={tsName}
            rows={rows}
            allColsMeta={allColsMeta}
            tsMeta={tsMeta}
            disabled={disabled}
            enabled={enabled}
            readOnly={readOnly}
            setTsData={setTsData}
            onPickMulti={onPickMulti}
            onPickTs={onPickTs}
          />
        );
      }

      case 'label':
        return <div key={`l-${idx}`} className={`py-1 ${disabled ? 'opacity-50' : ''}`}><Label className="text-xs">{el.text || ''}</Label></div>;

      default:
        return <div key={`unknown-${idx}`} className="text-xs text-destructive">Неизвестный: {el.type}</div>;
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3 w-full">
      {elements.length === 0 ? (
        <div className="text-xs text-muted-foreground p-3 border rounded bg-muted/20">
          Макет формы пуст — добавьте элементы в конструкторе форм (дерево реквизитов → холст)
        </div>
      ) : (
        elements.filter((el) => isVisibleOnDevice((el as any).visibleOn, device)).map((el, idx) => (
          <div key={`root-${idx}`} style={placementStyle((el as any).placement, 'vertical')} className="min-w-0 min-h-0">
            {renderElement(el, idx, formReadOnly, formEnabled, false, 0)}
          </div>
        ))
      )}
    </div>
  );
}

// ============================================================================
// GroupPagesView — группа в виде «Страницы»: вкладки-страницы (как в 1С).
// Активная страница — локальный state; данные (values/tsData) — общие,
// переключение страниц данные НЕ теряет.
// ============================================================================

function GroupPagesView({ elements, disabled, renderChild }: {
  elements: any[];
  disabled?: boolean;
  renderChild: (child: any, ci: number) => React.ReactNode;
}) {
  const [active, setActive] = useState(0);
  const safeActive = active < elements.length ? active : 0;
  return (
    <div className={`flex flex-col gap-2 ${disabled ? 'opacity-60' : ''}`}>
      {/* Вкладки-страницы */}
      <div className="flex gap-1 flex-wrap border-b pb-1">
        {elements.map((child: any, ci: number) => (
          <button
            key={ci}
            className={`text-[10px] px-2 py-1 rounded border ${
              safeActive === ci ? 'bg-primary/10 border-primary text-primary font-medium' : 'border-muted text-muted-foreground hover:bg-muted/30'
            }`}
            onClick={() => setActive(ci)}
          >
            {child.title || child.label || `Страница ${ci + 1}`}
          </button>
        ))}
      </div>
      {/* Активная страница (значения остальных сохраняются — данные общие) */}
      {elements[safeActive] !== undefined && renderChild(elements[safeActive], safeActive)}
    </div>
  );
}
